/**
 * BarberSchedule — shared constants and pure date/time helpers.
 * Extracted from `pages/BarberSchedule.js` to keep the container
 * focused on state + Supabase I/O.
 */

export const NOTICE_OPTIONS = [
  { value: 1,  label: '1 hour' },
  { value: 2,  label: '2 hours' },
  { value: 3,  label: '3 hours' },
  { value: 4,  label: '4 hours' },
  { value: 5,  label: '5 hours' },
  { value: 24, label: '1 day (24 hours)' },
  { value: 48, label: '2 days (48 hours)' },
];

export const WINDOW_OPTIONS = [
  { value: 7,  label: '1 week ahead' },
  { value: 14, label: '2 weeks ahead' },
  { value: 21, label: '3 weeks ahead' },
  { value: 28, label: '4 weeks ahead' },
  { value: 42, label: '6 weeks ahead' },
  { value: 56, label: '8 weeks ahead' },
  { value: 84, label: '12 weeks ahead' },
];

export const INTERVAL_OPTIONS = [
  { value: 0,  label: 'Match service duration (default)' },
  { value: 15, label: '15 minute grid' },
  { value: 30, label: '30 minute grid' },
  { value: 45, label: '45 minute grid' },
  { value: 60, label: '60 minute grid (1 hour)' },
];

export const BUFFER_OPTIONS = [
  { value: 0,  label: 'None' },
  { value: 5,  label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 20, label: '20 minutes' },
  { value: 30, label: '30 minutes' },
];

export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 12-month schedule horizon (spec-fixed). Independent of the customer
// booking window — the barber can look a year out even if customers
// can only book two weeks ahead.
export const HORIZON_MONTHS = 12;

// Full-date formatter, e.g. "Thursday, 20 August 2026".
export const fmtFullDate = (d) => new Intl.DateTimeFormat('en-GB', {
  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
}).format(d);

export const isoToDate = (iso) => new Date(iso + 'T00:00:00');

export const todayMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

export const isPastDay = (d) => {
  const t = todayMidnight();
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  return x < t;
};

export const horizonMax = () => {
  const d = todayMidnight();
  d.setMonth(d.getMonth() + HORIZON_MONTHS);
  return d;
};

export const isBeyondHorizon = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  return x > horizonMax();
};

// Monday-anchored start of the ISO week containing `d`.
export const startOfWeek = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const wd = x.getDay();               // 0=Sun..6=Sat
  const back = wd === 0 ? 6 : wd - 1;  // shift so Mon=0
  x.setDate(x.getDate() - back);
  return x;
};

// 7 dates Mon..Sun for the week containing `d`.
export const weekDays = (d) => {
  const start = startOfWeek(d);
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(start); x.setDate(start.getDate() + i); return x;
  });
};

// Days of the calendar month grid — 6 rows x 7 cols including leading/
// trailing padding so tiles align with the weekday headers.
export const monthGridDays = (d) => {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const leading = (first.getDay() + 6) % 7;
  const start = new Date(first); start.setDate(first.getDate() - leading);
  const out = [];
  for (let i = 0; i < 42; i++) {
    const x = new Date(start); x.setDate(start.getDate() + i);
    out.push(x);
  }
  return out;
};

export const timeToMin = (t) => {
  if (!t) return 0;
  const [hh, mm] = String(t).split(':').map(Number);
  return hh * 60 + (mm || 0);
};

export const minToTime = (m) => {
  const hh = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
